import React, { useEffect, useMemo } from 'react';
import cx from 'classnames';

import { Dropdown } from 'components';
import { XS } from 'components/typography';
import { Item } from 'components/form/Dropdown/Dropdown';
import { useTranslation } from 'react-i18next';
import { GroupType, SortDirection } from 'graphql-query-contracts';
import { useCostOffsetCodes } from 'react-hooks';
import { useChannelAdminContext } from 'hooks';

import styles from './CreateProductForm.scss';

const LIST_PER_PAGE = 200;

type CostOffsetCodeDropdownProps = {
  costOffsetCodeId?: string;
  updateDetails: (value: unknown, field: string) => void;
};

export function CostOffsetCodeDropdown({
  costOffsetCodeId,
  updateDetails,
}: CostOffsetCodeDropdownProps) {
  const { t } = useTranslation();
  const { channel } = useChannelAdminContext();
  const channelId = channel?._id;

  const { costOffsetCodes, fetchCostOffsetCodes, loading, error } =
    useCostOffsetCodes();

  const fetchVariables = useMemo(
    () => ({
      groupId: channelId ?? '',
      groupType: GroupType.GroupTypeActivateChannel,
      pagination: {
        perPage: LIST_PER_PAGE,
        start: 0,
      },
      sortBy: {
        key: 'code' as const,
        direction: SortDirection.Asc,
      },
    }),
    [channelId]
  );

  useEffect(() => {
    if (!channelId) return;

    fetchCostOffsetCodes(fetchVariables);
  }, [channelId, fetchCostOffsetCodes, fetchVariables]);

  const items: Item<string>[] | undefined = useMemo(() => {
    const none: Item<string> = {
      label: t('abp.productsServices.createProduct.costOffsetCode.none'),
      value: '',
    };

    const fromApi = costOffsetCodes.map(row => ({
      label: row.code,
      value: row.id,
    }));

    return [none, ...fromApi];
  }, [costOffsetCodes, t]);

  if (!channelId) {
    return null;
  }

  return (
    <>
      <div className={styles.label}>
        <label className={styles.descriptionLabel}>
          {t('abp.productsServices.createProduct.costOffsetCodeLabel')}
        </label>
      </div>
      <Dropdown
        testId="costOffsetCodeDropdown"
        doTranslation={false}
        className={cx(
          styles.costOffsetCodeSelect,
          styles.inputWithCustomLabel,
          styles.categoryDropdown
        )}
        value={costOffsetCodeId ?? ''}
        isLoading={loading}
        placeholder={t(
          'abp.productsServices.createProduct.costOffsetCode.placeholder'
        )}
        items={items}
        onChange={(item: { value: string }) => {
          const next = item.value === '' ? '' : item.value;

          updateDetails(next, 'costOffsetCodeId');
        }}
      />
      {error ? (
        <XS variant="secondary" block mt={0} mb={6}>
          {error.message}
        </XS>
      ) : (
        <XS variant="secondary" block mt={0} mb={6}>
          {t('abp.productsServices.createProduct.costOffsetCode.helperText')}
        </XS>
      )}
    </>
  );
}
